import { useState, useEffect } from 'react';
import { subscribeToDrills, subscribeToCategories } from '@/components/drill-library/services/drills-api';
import { Drill, Category } from '@/components/drill-library/types/drill-types';
import logger from '@/lib/logging';

interface UseDataResult<T> {
  data: T[];
  loading: boolean;
}

// Backward compatibility - returns just the data array
// NOTE: This hook is deprecated for drills/categories. Use useDataWithLoading instead.
export function useData<T>(collectionPath: string | null): T[] {
  const { data } = useDataWithLoading<T>(collectionPath);
  return data;
}

// New hook with loading state
export function useDataWithLoading<T>(collectionPath: string | null): UseDataResult<T> {
  const [data, setData] = useState<T[]>([]);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    // Skip Firebase operations during SSR
    if (typeof window === 'undefined') {
      setData([]);
      setLoading(false);
      return;
    }

    if (!collectionPath) {
      setData([]);
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      let unsubscribe: (() => void) | undefined;

      if (collectionPath === 'drills') {
        unsubscribe = subscribeToDrills(
          (drills) => {
            setData(drills as T[]);
            setLoading(false);
          },
          5000,
          (error) => {
            logger.error(`Error fetching collection ${collectionPath}:`, error);
            setLoading(false);
          }
        );
      } else if (collectionPath === 'categories') {
        unsubscribe = subscribeToCategories(
          (categories) => {
            setData(categories as T[]);
            setLoading(false);
          },
          5000,
          (error) => {
            logger.error(`Error fetching collection ${collectionPath}:`, error);
            setLoading(false);
          }
        );
      } else {
        logger.warn(
          `useDataWithLoading: Unsupported collection path "${collectionPath}". Use service functions for other collections.`
        );
        setLoading(false);
        return;
      }

      return () => {
        if (unsubscribe) {
          unsubscribe();
        }
      };
    } catch (error) {
      logger.error(`Error initializing collection ${collectionPath}:`, error);
      setLoading(false);
    }
  }, [collectionPath]);

  return { data, loading };
}

export type { Drill, Category };
